import imageCompression from 'browser-image-compression';

const COMPRESSION_OPTIONS = {
  maxWidthOrHeight: 4000,
  maxSizeMB: 8,
  initialQuality: 0.85,
  useWebWorker: true,
  preserveExif: false,
};

const SKIP_SIZE_BYTES = 1_000_000; // 1MB
const SKIP_MAX_DIMENSION = 4000;

async function shouldSkip(file: File): Promise<boolean> {
  if (file.size > SKIP_SIZE_BYTES) return false;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img.width <= SKIP_MAX_DIMENSION && img.height <= SKIP_MAX_DIMENSION);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve(false);
    };
    img.src = URL.createObjectURL(file);
  });
}

export async function compressImage(
  file: File,
  onProgress?: (progress: number) => void,
): Promise<File> {
  if (await shouldSkip(file)) {
    onProgress?.(100);
    return file;
  }

  const compressed = await imageCompression(file, {
    ...COMPRESSION_OPTIONS,
    onProgress,
  });

  return new File([compressed], file.name, {
    type: compressed.type,
    lastModified: file.lastModified,
  });
}

export async function compressImages(
  files: File[],
  onProgress?: (progress: number) => void,
): Promise<File[]> {
  if (files.length === 0) return files;

  let completedProgress = 0;

  const results = await Promise.all(
    files.map((file) =>
      compressImage(file, (fileProgress) => {
        // Approximate batch progress: each file contributes equally
        const batchProgress =
          (completedProgress + fileProgress / files.length);
        onProgress?.(Math.round(batchProgress));
      }).then((result) => {
        completedProgress += 100 / files.length;
        return result;
      }),
    ),
  );

  onProgress?.(100);
  return results;
}
